'use client';

import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { useEffect, useRef, useState, useCallback } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MapPin, Briefcase, Clock, ChevronRight, Star, Zap, Users } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import VerificationBadge from '@/components/VerificationBadge';

// ── Types ─────────────────────────────────────────────────────────────────────
interface LiveJob {
  id: string; title: string; jobType: string;
  city?: string; country?: string;
  salaryMin?: number; salaryMax?: number; salaryCurrency: string;
  publishedAt?: string; isBiddable?: boolean;
  recruiter: { companyName: string; logoUrl?: string; isVerified: boolean };
}

interface LiveFreelancer {
  id: string; profileSlug: string; firstName: string; lastName: string;
  headline?: string; profilePhoto?: string; coverPhoto?: string; country?: string; city?: string;
  isAvailableForWork: boolean;
  isVerifiedProfessional: boolean;
  salaryExpectationMin?: number;
  salaryExpectationMax?: number;
  salaryCurrency: string;
  skills: { skill: { name: string } }[];
  portfolio: { id: string; title: string; imageUrl?: string }[];
  _count: { portfolio: number; experience: number };
}

const JOB_TYPE_PT: Record<string, string> = {
  FULL_TIME: 'Tempo Inteiro', PART_TIME: 'Tempo Parcial', FREELANCE: 'Freelance', CONTRACT: 'Contrato',
};

function timeAgo(dateStr?: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d atrás`;
  if (h > 0) return `${h}h atrás`;
  return 'Agora mesmo';
}

// ── Infinite scroll hook ───────────────────────────────────────────────────────
function useInfiniteScroll<T>(
  fetchUrl: (page: number) => string,
  extractItems: (json: Record<string, unknown>) => T[],
  enabled: boolean,
) {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const loadPage = useCallback(async (pg: number) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch(fetchUrl(pg));
      const json = await res.json() as Record<string, unknown>;
      if (json.success) {
        const newItems = extractItems(json);
        setItems(prev => pg === 1 ? newItems : [...prev, ...newItems]);
        const data = json.data as Record<string, unknown>;
        const totalPages = Number(data.totalPages ?? 1);
        setHasMore(pg < totalPages);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [fetchUrl, extractItems, loading]); // eslint-disable-line

  // Initial load
  useEffect(() => {
    if (!enabled) return;
    setItems([]);
    setPage(1);
    setHasMore(true);
    fetch(fetchUrl(1))
      .then(r => r.json())
      .then((json: Record<string, unknown>) => {
        if (json.success) {
          setItems(extractItems(json));
          const data = json.data as Record<string, unknown>;
          setHasMore(Number(data.totalPages ?? 1) > 1);
        }
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [enabled, fetchUrl]); // eslint-disable-line

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!enabled || !hasMore) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && hasMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadPage(nextPage);
        }
      },
      { threshold: 0.1 },
    );
    const el = sentinelRef.current;
    if (el) obs.observe(el);
    return () => { if (el) obs.unobserve(el); };
  }, [enabled, hasMore, loading, page, loadPage]);

  return { items, loading, hasMore, sentinelRef };
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function Home() {
  const { data: session, status } = useSession();
  const { t } = useTranslation();
  const [stats, setStats] = useState({ totalFreelancers: 0, totalActiveJobs: 0, totalRecruiters: 0, totalCountries: 5 });
  const [statsLoaded, setStatsLoaded] = useState(false);

  const isLoggedIn = status === 'authenticated';
  const role = session?.user?.role as string | undefined;
  const isFreelancer = role === 'FREELANCER';
  const isRecruiter = role === 'RECRUITER';

  // Jobs - shown for freelancers (full infinite) and guests (first 6 only)
  const jobFetchUrl = useCallback(
    (pg: number) => `/api/v1/jobs?limit=${isFreelancer ? 10 : 6}&status=APPROVED&page=${pg}`,
    [isFreelancer],
  );
  const jobExtract = useCallback(
    (json: Record<string, unknown>) => ((json.data as Record<string, unknown>).jobs as LiveJob[]) ?? [],
    [],
  );
  const jobsScroll = useInfiniteScroll<LiveJob>(jobFetchUrl, jobExtract, !isRecruiter);

  // Freelancers - shown for recruiters (full infinite) and guests (first 6 only)
  const freelancerFetchUrl = useCallback(
    (pg: number) => `/api/v1/public/freelancers?limit=${isRecruiter ? 12 : 8}&page=${pg}`,
    [isRecruiter],
  );
  const freelancerExtract = useCallback(
    (json: Record<string, unknown>) => ((json.data as Record<string, unknown>).freelancers as LiveFreelancer[]) ?? [],
    [],
  );
  const freelancersScroll = useInfiniteScroll<LiveFreelancer>(freelancerFetchUrl, freelancerExtract, !isFreelancer);

  // Stats
  useEffect(() => {
    fetch('/api/v1/public/stats')
      .then(r => r.json())
      .then(json => { if (json.success) { setStats(json.data); setStatsLoaded(true); } })
      .catch(() => { setStatsLoaded(true); }); // show real 0 on error too
  }, []);

  const jobs = jobsScroll.items;
  const freelancers = freelancersScroll.items;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-r from-blue-900 via-teal-700 to-teal-600 text-white py-16 sm:py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 md:mb-6 leading-tight">
            {isFreelancer
              ? t('home.heroTitleFreelancer')
              : isRecruiter
                ? t('home.heroTitleRecruiter')
                : t('home.heroTitleGuest')}
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-8 opacity-95 max-w-2xl mx-auto">
            {isFreelancer
              ? t('home.heroSubFreelancer')
              : isRecruiter
                ? t('home.heroSubRecruiter')
                : t('home.heroSubGuest')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href={isLoggedIn ? '/dashboard' : '/register/seeker'}
              className="inline-block bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition text-base">
              {isLoggedIn ? t('home.heroBtnDashboard') : t('home.heroBtnRegisterFreelancer')}
            </Link>
            <Link href={isLoggedIn ? (isFreelancer ? '/jobs' : '/freelancers') : '/register/recruiter'}
              className="inline-block border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition text-base">
              {isLoggedIn
                ? (isFreelancer ? t('home.heroBtnBrowseJobs') : t('home.heroBtnBrowseFreelancers'))
                : t('home.heroBtnRegisterRecruiter')}
            </Link>
          </div>
        </div>
      </section>

      {/* ── Stats bar ─────────────────────────────────────────────────────── */}
      <section className="bg-white border-b border-gray-100 py-8 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { label: t('home.statsFreelancers'), value: statsLoaded ? `${stats.totalFreelancers.toLocaleString()}+` : '-' },
            { label: t('home.statsJobs'), value: statsLoaded ? `${stats.totalActiveJobs.toLocaleString()}+` : '-' },
            { label: t('home.statsCompanies'), value: statsLoaded ? `${stats.totalRecruiters.toLocaleString()}+` : '-' },
            { label: t('home.statsCountries'), value: stats.totalCountries.toString() },
          ].map(s => (
            <div key={s.label}>
              <div className="text-3xl font-bold text-teal-600">{s.value}</div>
              <div className="text-sm text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Jobs section (hidden for recruiters) ─────────────────────────── */}
      {!isRecruiter && (
        <section className="py-16 sm:py-24 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-12 flex items-center justify-between">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                  {t('home.jobsSectionTitle')}
                </h2>
                <p className="text-gray-600 text-lg">
                  {isFreelancer ? t('home.jobsSubFreelancer') : t('home.jobsSubGuest')}
                </p>
              </div>
              <Link href="/jobs" className="hidden sm:flex items-center gap-1 text-teal-600 hover:text-teal-700 font-medium text-sm">
                {t('home.viewAll')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {jobsScroll.loading && jobs.length === 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 h-32 animate-pulse" />
                ))}
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <Briefcase className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>{t('home.noJobs')}</p>
                <Link href="/register/recruiter" className="mt-4 inline-block text-teal-600 hover:underline text-sm">
                  {t('home.postJobLink')}
                </Link>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {jobs.map(job => (
                    <Link key={job.id} href={`/jobs/${job.id}`}
                      className="bg-white rounded-xl border border-gray-200 p-6 flex gap-4 hover:shadow-md hover:border-teal-200 transition group">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {job.recruiter.logoUrl
                          ? <img src={job.recruiter.logoUrl} alt="" className="w-full h-full object-cover" />
                          : <div className="w-full h-full bg-teal-100 flex items-center justify-center text-lg font-bold text-teal-700">{job.recruiter.companyName[0]}</div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 mb-1">
                          <h3 className="text-base font-bold text-gray-900 group-hover:text-teal-700 transition line-clamp-1">{job.title}</h3>
                          {job.isBiddable && (
                            <span className="flex-shrink-0 inline-flex items-center gap-0.5 text-xs bg-teal-50 text-teal-700 border border-teal-200 px-1.5 py-0.5 rounded-full font-semibold">
                              <Zap className="w-3 h-3" />Bid
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{job.recruiter.companyName}</p>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{JOB_TYPE_PT[job.jobType] ?? job.jobType}</span>
                          {(job.city || job.country) && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{[job.city, job.country].filter(Boolean).join(', ')}</span>}
                          {job.publishedAt && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(job.publishedAt)}</span>}
                        </div>
                      </div>
                      {(job.salaryMin || job.salaryMax) && (
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-bold text-gray-900">
                            {job.salaryCurrency} {(job.salaryMin ?? job.salaryMax) !== undefined
                              ? Number(job.salaryMin ?? job.salaryMax).toLocaleString('pt-AO').replace(/[\u00a0\s]/g, '.')
                              : ''}
                            {job.salaryMax && job.salaryMin && job.salaryMax !== job.salaryMin ? '+' : ''}
                          </div>
                          <div className="text-xs text-gray-400">{t('home.perMonth')}</div>
                        </div>
                      )}
                    </Link>
                  ))}
                </div>

                {/* Infinite scroll sentinel - only for freelancers */}
                {isFreelancer && (
                  <>
                    <div ref={jobsScroll.sentinelRef} className="h-4 mt-4" />
                    {jobsScroll.loading && (
                      <div className="flex justify-center py-8">
                        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                    {!jobsScroll.hasMore && jobs.length > 0 && (
                      <p className="text-center text-gray-400 text-sm py-6">{t('home.noMoreJobs')}</p>
                    )}
                  </>
                )}

                {/* Guest: load more button */}
                {!isLoggedIn && (
                  <div className="text-center mt-10">
                    <Link href="/jobs"
                      className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-teal-700 transition">
                      {t('home.browseAllJobs')} <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* ── Freelancers section (hidden for freelancers) ──────────────────── */}
      {!isFreelancer && (
        <section className="py-16 sm:py-20 px-4 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto">
            <div className="mb-10 flex items-center justify-between">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                  {isRecruiter ? t('home.freelancersSectionTitleRecruiter') : t('home.freelancersSectionTitleGuest')}
                </h2>
                <p className="text-gray-600">
                  {isRecruiter ? t('home.freelancersSubRecruiter') : t('home.freelancersSubGuest')}
                </p>
              </div>
              <Link href="/portfolios" className="hidden sm:flex items-center gap-1 text-teal-600 hover:text-teal-700 font-medium text-sm">
                {t('home.viewAll')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {freelancersScroll.loading && freelancers.length === 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-200 overflow-hidden animate-pulse">
                    <div className="h-40 bg-gray-100" />
                    <div className="p-4 space-y-2">
                      <div className="h-4 bg-gray-100 rounded w-3/4" />
                      <div className="h-3 bg-gray-100 rounded w-1/2" />
                      <div className="h-3 bg-gray-100 rounded w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : freelancers.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>{t('home.noFreelancers')}</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                  {freelancers.map(f => (
                    <HomepageFreelancerCard key={f.id} f={f} />
                  ))}
                </div>

                {/* Infinite scroll sentinel - only for recruiters */}
                {isRecruiter && (
                  <>
                    <div ref={freelancersScroll.sentinelRef} className="h-4 mt-4" />
                    {freelancersScroll.loading && (
                      <div className="flex justify-center py-8">
                        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                    {!freelancersScroll.hasMore && freelancers.length > 0 && (
                      <p className="text-center text-gray-400 text-sm py-6">{t('home.noMoreFreelancers')}</p>
                    )}
                  </>
                )}

                {/* Guest: view all button */}
                {!isLoggedIn && (
                  <div className="text-center mt-10">
                    <Link href="/portfolios"
                      className="inline-flex items-center gap-2 border border-teal-600 text-teal-600 px-8 py-3 rounded-xl font-semibold hover:bg-teal-50 transition">
                      {t('home.browseAllFreelancers')} <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      {!isLoggedIn && (
        <section className="bg-gradient-to-br from-teal-600 to-blue-800 text-white py-16 sm:py-24 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-4">
              <Star className="w-10 h-10 text-amber-400" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">
              {t('home.ctaTitle')}
            </h3>
            <p className="text-lg opacity-90 mb-8">
              {t('home.ctaSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/register/seeker" className="bg-white text-teal-600 px-8 py-3 rounded-xl hover:bg-gray-50 transition font-semibold">
                {t('home.ctaBtnFreelancer')}
              </Link>
              <Link href="/register/recruiter" className="border-2 border-white text-white px-8 py-3 rounded-xl hover:bg-white/10 transition font-semibold">
                {t('home.ctaBtnRecruiter')}
              </Link>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

// ── HomepageFreelancerCard ─────────────────────────────────────────────────────
function HomepageFreelancerCard({ f }: { f: LiveFreelancer }) {
  const name = `${f.firstName} ${f.lastName}`;
  const initials = `${f.firstName[0] ?? ''}${f.lastName[0] ?? ''}`.toUpperCase();
  const topPortfolio = f.portfolio[0];

  return (
    <a href={`/freelancers/${f.profileSlug}`} className="group block">
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-teal-200 transition-all duration-200">
        {/* Cover image → portfolio image → gradient placeholder */}
        <div className="relative h-40 bg-gradient-to-br from-teal-50 to-blue-50 overflow-hidden">
          {f.coverPhoto ? (
            <img src={f.coverPhoto} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : topPortfolio?.imageUrl ? (
            <img src={topPortfolio.imageUrl} alt={topPortfolio.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-teal-600 flex items-center justify-center text-2xl font-black text-white">
                {initials}
              </div>
            </div>
          )}
          {f.isAvailableForWork && (
            <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow">
              Disponível
            </div>
          )}
          {f._count.portfolio > 0 && (
            <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm">
              {f._count.portfolio} projeto{f._count.portfolio !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div className="p-4">
          <div className="flex items-center gap-3 mb-2">
            {f.profilePhoto ? (
              <img src={f.profilePhoto} alt={name} className="w-9 h-9 rounded-full object-cover border-2 border-white shadow flex-shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-teal-600 flex items-center justify-center text-sm font-black text-white flex-shrink-0">
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="font-bold text-gray-900 text-sm truncate">{name}</div>
                {f.isVerifiedProfessional && <VerificationBadge size="sm" showLabel={false} />}
              </div>
              {(f.city || f.country) && (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <MapPin className="w-3 h-3" />
                  {[f.city, f.country].filter(Boolean).join(', ')}
                </div>
              )}
            </div>
          </div>

          {f.headline && (
            <p className="text-xs text-gray-600 line-clamp-2 mb-3">{f.headline}</p>
          )}

          {f.skills.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {f.skills.slice(0, 3).map((s, i) => (
                <span key={i} className="text-xs bg-teal-50 text-teal-700 font-medium px-2 py-0.5 rounded-full border border-teal-100">
                  {s.skill.name}
                </span>
              ))}
              {f.skills.length > 3 && (
                <span className="text-xs text-gray-400">+{f.skills.length - 3}</span>
              )}
            </div>
          )}

          {(f.salaryExpectationMin || f.salaryExpectationMax) && (
            <div className="text-xs font-bold text-teal-600 border-t border-gray-100 pt-3 mt-1">
              {f.salaryCurrency} {(f.salaryExpectationMin ?? f.salaryExpectationMax) !== undefined
                ? Number(f.salaryExpectationMin ?? f.salaryExpectationMax).toLocaleString('pt-AO').replace(/[\u00a0\s]/g, '.')
                : ''}
              {f.salaryExpectationMax && f.salaryExpectationMin && f.salaryExpectationMax !== f.salaryExpectationMin
                ? `\u2013${Number(f.salaryExpectationMax).toLocaleString('pt-AO').replace(/[\u00a0\s]/g, '.')}` : ''}
              <span className="font-normal text-gray-400"> /mês</span>
            </div>
          )}
        </div>
      </div>
    </a>
  );
}
